import { NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule , MatPseudoCheckboxModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';
import { Observable } from 'rxjs';
import { isNil } from '@app/utils';
import { Validations } from '../..';

@Component({
    selector: 'formly-multiselect-chips',
    templateUrl: './multiselect-chips.component.html',
    styleUrls: ['./multiselect-chips.component.less'],
    standalone: true,
    imports: [
        NgIf,
        MatOptionModule,
        MatPseudoCheckboxModule,
        MatFormFieldModule,
        MatSelectModule,
        FormsModule,
        ReactiveFormsModule,
        NgTemplateOutlet,
        NgFor,
        MatChipsModule,
        NgClass,
        MatIconModule,
    ],
})

/**
 * Formly Multi Select Chips Component
 *
 * Custom Template Options:
 * - selectAllLabel: string: allows you to customize the description for "All".
 * - allowSelectAll: boolean: allowSelectAll = true will allow the user to select all items in the select box.
 * - allowRemoveChip: boolean: allowRemoveChip = true will enable the close "X" on a chip for removal.
 * - allowClearAllChip: boolean: allowClearAllChip = true will give the user a "Clear All" chip to quickly clear all chips.
 * - excludeChipsWhenAll: boolean: excludeChipsWhenAll = true will not show chips when "All" is selected.
 * - scrollAfter3Rows: boolean: scrollAfter3Rows = true will add a scrollbar after 3 rows of chips.
 * - scrollAfter6Rows: boolean: scrollAfter6Rows = true will add a scrollbar after 6 rows of chips.
 */
export class FormlyMultiSelectChipsComponent extends FieldType<FormlyFieldConfig> implements OnInit {
	selectOptions: any[] = [];

	public ngOnInit(): void {
		if (!this.value) {
			this.value = [];
		}
		if (this.props.options instanceof Array) {
			this.selectOptions = this.props.options;
		} else if (this.props.options instanceof Observable) {
			this.props.options.subscribe((result) => {
				this.selectOptions = result;
			});
		}
	}

	get selections(): any[] {
		if (this.value && this.value.length > 0) {
			return this.selectOptions.filter((f) => this.value.includes(f.value));
		}
		return [];
	}

	get selectAllLabel(): string {
		if (this.props.allowSelectAll) {
			return !(this.props.selectAllLabel === null || this.props.selectAllLabel === undefined) ? this.props.selectAllLabel : 'All';
		}
		return '';
	}

	get state() {
		if (this.value.length > 0) {
			return this.value.length !== this.selectOptions.length ? 'indeterminate' : 'checked';
		}

		return '';
	}

	get canEdit() {
		return !(this.props.disabled === true || this.props.readonly === true);
	}

	toggleSelectAll() {
		this.formControl.setValue(this.value.length !== this.selectOptions.length ? this.selectOptions.map((x) => x.value) : []);
		this.formControl.markAsDirty();
	}

	getSelectedDisplay(): string {
		if (
			!isNil(this.props.excludeChipsWhenAll) &&
			this.props.excludeChipsWhenAll === true &&
			this.value.length > 0 &&
			this.value.length === this.selectOptions.length
		) {
			return 'All';
		} else {
			return `${this.value.length} Selected`;
		}
	}

	get requiredErrorMessage(): string {
		if (this.formControl.errors?.required) {
			return Validations.requiredValidationMessage({}, this.field);
		}

		return '';
	}

	removeChip(chip: any, index: number): void {
		let options = this.value as any[];
		if (index === -1 && this.props.allowClearAllChip) {
			options = [];
		} else {
			options.splice(index, 1);
		}
		if (this.props.removed) {
			options = this.props.removed(chip) ?? options;
		}
		this.formControl.setValue(options);
	}

	handleCheckboxSelection(value: number): void {
		if (typeof this.props.changeMoreCheckboxs == 'function') {
			this.props.changeMoreCheckboxs(value);
		}
	}
}
